<template>
  <div>
    <v-layout>
      <v-main :class="mainClassObject">
        <OfflineBanner/>
        <AppBanners/>
        <!-- Dialogs -->
        <AccountMarkedForDeletionDialog/>
        <TrialHasEndedDialog/>

        <!-- Loader -->
        <div id="app-loader">
          <div class="app-logo">
            <img :src="fleetwireLogo" alt="Fleetwire Logo">
          </div>
          <span>Loading application...</span>
        </div>

        <slot></slot>
      </v-main>
    </v-layout>
  </div>
</template>

<script lang="ts" setup>
import {computed, defineAsyncComponent, onBeforeMount, onUnmounted, ref, watchEffect} from 'vue'
import {useStore} from 'vuex';
import fleetwireLogo from '@/../images/fleetwire-logo-new.png';
import {useAuth} from '@/composables/useAuth';
import {useAppSettings} from '@/composables/useAppSettings';
import {addGtag} from 'vue-gtag';
import OfflineBanner from '@/components/dashboard/Banners/OfflineBanner.vue';

const AccountMarkedForDeletionDialog = defineAsyncComponent(() => import('@/components/dashboard/dialogs/AccountMarkedForDeletionDialog.vue'));
const TrialHasEndedDialog = defineAsyncComponent(() => import('@/components/dashboard/dialogs/TrialHasEndedDialog.vue'));
const AppBanners = defineAsyncComponent(() => import('@/components/dashboard/AppBanners.vue'));

const store = useStore();
const {fetchSession} = useAuth();
const {fetchBuildNumber} = useAppSettings();

/** Reactive state for booting */
const booting = computed<boolean>(() => store.state.app.booting);
const rightSidebarOpen = computed<boolean>(() => store.state.app.rightSidebar);
const mainClassObject = computed(() => ({
  'booting': booting.value,
  'right-bar-enabled': rightSidebarOpen.value
}));

console.log('🚀 TheDashboardLayout Initialized');


const pollInterval = ref<number | null>(null);
const pollTimeout = 1000 * 60 * 5; // 5 minutes
const pollCount = ref(0);

const startPolling = () => {
  console.dir('⏱️ Starting Polling');
  pollInterval.value = window.setInterval(() => {
    pollCount.value++;
    console.dir(`⏱️ Polling... (${pollCount.value})`);
    fetchBuildNumber();
  }, pollTimeout);
};

// ✅ Cleanup on unmount
onUnmounted(() => {
  if (pollInterval.value) {
    clearInterval(pollInterval.value);
    console.log('🛑 Polling Stopped!');
  }
});

/** Fetch session on mount */
onBeforeMount(() => {
  fetchSession();
  startPolling();

  /** Initialize Google Analytics (gtag) */
  if (navigator.onLine) {
    addGtag()
      .then(() => console.log('✅ Google Analytics (gtag) Bootstrapped'))
      .catch(() => console.warn('⚠️ Google Analytics failed to load'));
  }
});
/** Watch for booting changes */
watchEffect(() => {
  console.log(`🕒 Booting Status: ${booting.value}`);
});
</script>

<style scoped>

.v-main.booting #app-loader {
  display: block;
}

#app-loader {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #fff;
  z-index: 9999;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}

#app-loader .app-logo {
  position: absolute;
  top: 50%;
  left: 50%;
  text-align: center;
  width: 100px;
  height: 140px;
  margin-left: -52px;
  margin-top: -70px;
}

#app-loader .app-logo img {
  width: 100px;
}

#app-loader > span {
  color: #181818;
  font-size: 20px;
  text-align: center;
  display: block;
  width: 200px;
  position: absolute;
  top: 50%;
  left: 50%;
  margin-left: -100px;
  margin-top: 75px;
}

</style>